import { createContext, Dispatch, useCallback, useContext, useReducer } from 'react'

type EbolMatchingState = {
  selectedOrderId?: string
  selectedBolId?: string
  selectedEbolId?: string
  isOpen: boolean
  hasJustMatched?: boolean //kicks off a "success/close transition"
}

export type ViewBolArgs = { orderId?: string; bolId?: string }

type Action =
  | { type: 'viewAll' }
  | { type: 'close' }
  | ({ type: 'viewBol' } & ViewBolArgs)
  | { type: 'selectEbol'; ebolId: string }
  | { type: 'match' }

export const reducer = (state: EbolMatchingState, action: Action): EbolMatchingState => {
  switch (action.type) {
    case 'close':
      return { isOpen: false }
    case 'viewAll':
      return { isOpen: true }
    case 'viewBol': {
      const orderId = action.orderId ?? state.selectedOrderId
      if (!orderId) {
        console.error('Selected Order Id is required before selecting a BOL')
        return { isOpen: false }
      }
      return { selectedOrderId: orderId, isOpen: !!action.bolId, selectedBolId: action.bolId }
    }
    case 'selectEbol': {
      return { ...state, selectedEbolId: action.ebolId }
    }
    case 'match': {
      return { isOpen: true, hasJustMatched: true }
    }
  }
}

export type EbolMatchingContextType = EbolMatchingState & {
  close: () => void
  viewAll: () => void
  viewBol: (args: ViewBolArgs) => void
  selectEbol: (id: string) => void
  match: () => void
}
export const EbolMatchingContext = createContext<EbolMatchingContextType>({
  isOpen: false,
  close: () => {},
  viewAll: () => {},
  viewBol: () => {},
  selectEbol: () => {},
  match: () => {},
})

export const useCreateEbolMatchingContext = () => {
  const [{ selectedBolId, selectedOrderId, isOpen, selectedEbolId, hasJustMatched }, dispatch] = useReducer(reducer, {
    isOpen: false,
  })
  const close = useCallback(() => dispatch({ type: 'close' }), [])
  const viewAll = useCallback(() => dispatch({ type: 'viewAll' }), [])
  const viewBol = useCallback((args: ViewBolArgs) => dispatch({ ...args, type: 'viewBol' }), [])
  const selectEbol = useCallback((id: string) => dispatch({ type: 'selectEbol', ebolId: id }), [])
  const match = useCallback(() => dispatch({ type: 'match' }), [])
  return {
    selectedBolId,
    selectedOrderId,
    isOpen,
    selectedEbolId,
    hasJustMatched,
    close,
    viewAll,
    viewBol,
    selectEbol,
    match,
  }
}

export const useEbolMatchingContext = () => {
  const context = useContext(EbolMatchingContext)
  if (!context) {
    throw new Error('useEbolMatchingContext must be used within a EBOLMatchingProvider')
  }
  return context
}
